import React from "react";
import CachedImage from "./ui/CachedImage";
import { getTeamHelmetUrl } from "../utils/helmetGenerator";
import { formatGameDate } from "../utils/dateUtils";

const GameCard = ({ game, onMakePick, showPickControls, userPick }) => {
  const { homeTeam, awayTeam, gameDate, status } = game;

  const getStatusBadge = () => {
    switch (status) {
      case "scheduled":
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
            Upcoming
          </span>
        );
      case "in_progress":
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
            Live
          </span>
        );
      case "completed":
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
            Final
          </span>
        );
      case "postponed":
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
            Unknown
          </span>
        );
      case "cancelled":
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
            Unknown
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
            Unknown
          </span>
        );
    }
  };

  const isCorrectPick = () => {
    if (!userPick || status !== "completed") return null;

    return userPick.isCorrect;
    // const winningTeamId = result?.winningTeamId;
    //
    // if (winningTeamId === null) return 'push'; // Tie game
    // return userPickedTeamId === winningTeamId;
  };

  const renderPickResult = () => {
    const pickResult = isCorrectPick();
    if (pickResult === null) return null;

    if (pickResult === "push") {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
          Push
        </span>
      );
    }

    return pickResult ? (
      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
        Correct
      </span>
    ) : (
      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
        Incorrect
      </span>
    );
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden transition-all duration-200 hover:shadow-md">
      {/* Game header */}
      <div className="p-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
        <div className="text-sm text-gray-600">{formatGameDate(gameDate)}</div>
        <div className="flex space-x-2">
          {getStatusBadge()}
          {renderPickResult()}
        </div>
      </div>

      {/* Teams */}
      <div className="p-4">
        {/* Away team */}
        <div className="flex items-center mb-4">
          <div className="w-12 h-12 flex-shrink-0 mr-3">
            <CachedImage
              src={awayTeam.logoUrl || getTeamHelmetUrl(awayTeam)}
              fallbackSrc={getTeamHelmetUrl(awayTeam)}
              alt={awayTeam.name}
              className="w-full h-full"
            />
          </div>
          <div className="flex-grow">
            <div className="text-xs text-gray-500 uppercase tracking-wide">
              Away
            </div>
            <div className="font-bold">
              {awayTeam.city} {awayTeam.name}
            </div>
            <div className="text-sm text-gray-600">{awayTeam.abbreviation}</div>
          </div>
          <div className="text-xl font-bold">
            {status !== "scheduled" ? game.awayScore || 0 : ""}
          </div>
        </div>

        {/* Home team */}
        <div className="flex items-center">
          <div className="w-12 h-12 flex-shrink-0 mr-3">
            <CachedImage
              src={homeTeam.logoUrl || getTeamHelmetUrl(homeTeam)}
              fallbackSrc={getTeamHelmetUrl(homeTeam)}
              alt={homeTeam.name}
              className="w-full h-full"
            />
          </div>
          <div className="flex-grow">
            <div className="text-xs text-gray-500 uppercase tracking-wide">
              Home
            </div>
            <div className="font-bold">
              {homeTeam.city} {homeTeam.name}
            </div>
            <div className="text-sm text-gray-600">{homeTeam.abbreviation}</div>
          </div>
          <div className="text-xl font-bold">
            {status !== "scheduled" ? game.homeScore || 0 : ""}
          </div>
        </div>

        {/* Betting line info */}
        {game.currentLine && (
          <div className="mt-4 pt-3 border-t border-gray-200 text-sm text-gray-600">
            <div className="flex justify-between">
              <span>Spread:</span>
              <span>
                {homeTeam.abbreviation}{" "}
                {game.currentLine.homeSpread > 0 ? "+" : ""}
                {game.currentLine.homeSpread}
              </span>
            </div>
            <div className="flex justify-between">
              <span>O/U:</span>
              <span>{game.currentLine.overUnder}</span>
            </div>
          </div>
        )}
      </div>

      {/* Pick controls */}
      {showPickControls && status === "scheduled" && (
        <div className="p-4 bg-gray-50 border-t border-gray-200">
          <div className="flex space-x-2">
            <button
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium ${
                userPick?.selectedTeam?.id === awayTeam.id
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-800 hover:bg-gray-300"
              }`}
              onClick={() => onMakePick(game.id, awayTeam.abbreviation)}
              disabled={status !== "scheduled"}
            >
              {awayTeam.abbreviation}
            </button>
            <button
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium ${
                userPick?.selectedTeam?.id === homeTeam.id
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-800 hover:bg-gray-300"
              }`}
              onClick={() => onMakePick(game.id, homeTeam.abbreviation)}
              disabled={status !== "scheduled"}
            >
              {homeTeam.abbreviation}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameCard;
